<template>
    <div v-if="numberOfPages > 1" class="dk-pagination d--flex">
        <div
            :class="{
                'ts--body ts-weight--700': true,
                'dk-pagination__control-btn': true,
                'dk-pagination__control-btn--active': isPrevBtnActive,
            }"
            @click="handleClick(currentPage - 1)"
        >
            {{ $t('dk-pagination.button.previous') }}
        </div>
        <div class="dk-pagination__paging d--flex m-left--m m-right--m">
            <div
                :class="{
                    'btn btn--round m-right--xxs m-left--xxs ts--body ts-weight--700': true,
                    'dk-pagination__paging-item': true,
                    'dk-pagination__paging-item--active': isPageActive(firstPage),
                }"
                @click="handleClick(firstPage)"
            >
                {{ firstPage }}
            </div>
            <div
                v-if="!shouldHidePlaceholders && shouldShowAfterPlaceholder"
                class="dk-pagination__paging-placeholder m-right--xxs m-left--xxs color--grey-900 ts-weight--700 ts--body"
            >
                &hellip;
            </div>
            <div
                v-for="(pageItem, i) in visiblePages"
                :key="i"
                :class="{
                    'btn btn--round m-right--xxs m-left--xxs ts--body ts-weight--700': true,
                    'dk-pagination__paging-item': true,
                    'dk-pagination__paging-item--active': isPageActive(pageItem),
                }"
                @click="handleClick(pageItem)"
            >
                {{ pageItem }}
            </div>
            <div
                v-if="!shouldHidePlaceholders && shouldShowBeforePlaceholder"
                class="dk-pagination__paging-placeholder m-right--xxs m-left--xxs color--grey-900 ts-weight--700 ts--body"
            >
                &hellip;
            </div>
            <div
                :class="{
                    'btn btn--round m-right--xxs m-left--xxs ts--body ts-weight--700': true,
                    'dk-pagination__paging-item': true,
                    'dk-pagination__paging-item--active': isPageActive(numberOfPages),
                }"
                @click="handleClick(numberOfPages)"
            >
                {{ numberOfPages }}
            </div>
        </div>
        <div
            :class="{
                'ts--body ts-weight--700': true,
                'dk-pagination__control-btn': true,
                'dk-pagination__control-btn--active': isNextBtnActive,
            }"
            @click="handleClick(currentPage + 1)"
        >
            {{ $t('dk-pagination.button.next') }}
        </div>
    </div>
</template>

<script>
/**
 * A component for displaying pagination.
 *
 * @version 1.0.0
 * @example ./../examples/DkPagination.md
 */
export default {
    name: 'DkPagination',
    props: {
        /**
         * Number of entities to show per page.
         */
        perPage: {
            type: [Number, String],
            default: 9,
        },
        /**
         * Total number of entities
         */
        totalEntities: {
            type: [Number, String],
            required: true,
        },
        /**
         * Current page number
         */
        page: {
            type: Number,
            default: 1,
        },
    },
    data() {
        return {
            currentPage: 1,
            firstPage: 1,
            pageShift: 4,
        };
    },
    computed: {
        numberOfPages() {
            const perPage = parseInt(this.perPage, 10);
            const totalEntities = parseInt(this.totalEntities, 10);

            if (perPage < 1 || totalEntities <= 1) {
                return 0;
            }

            return Math.ceil(totalEntities / perPage);
        },
        isPrevBtnActive() {
            return this.currentPage !== 1;
        },
        isNextBtnActive() {
            return this.currentPage !== parseInt(this.numberOfPages, 10);
        },
        shouldHidePlaceholders() {
            if (this.numberOfPages <= this.pageShift) {
                return true;
            }

            return false;
        },
        shouldShowAfterPlaceholder() {
            if (this.currentPage < this.pageShift) {
                return false;
            }

            return true;
        },
        shouldShowBeforePlaceholder() {
            if (this.currentPage > this.numberOfPages - (this.pageShift - 1)) {
                return false;
            }

            return true;
        },
        visiblePages() {
            const result = [];

            if (this.shouldHidePlaceholders) {
                for (let i = this.firstPage + 1; i < this.numberOfPages; i += 1) {
                    result.push(i);
                }

                return result;
            }
            if (this.currentPage < this.pageShift) {
                for (let i = this.firstPage + 1; i <= this.pageShift; i += 1) {
                    result.push(i);
                }

                return result;
            }
            if (this.currentPage > this.numberOfPages - (this.pageShift - 1)) {
                for (let i = this.numberOfPages - (this.pageShift - 1); i < this.numberOfPages; i += 1) {
                    result.push(i);
                }
                return result;
            }

            return [this.currentPage - 1, this.currentPage, this.currentPage + 1];
        },
    },
    watch: {
        page(pageNumber) {
            this.$set(this, 'currentPage', pageNumber);
        },
    },
    created() {
        this.currentPage = this.page;
    },
    methods: {
        handleClick(page) {
            const currentPage = parseInt(page, 10);

            if (isNaN(currentPage)) {
                return;
            }

            if (currentPage < 1 || currentPage > this.numberOfPages) {
                return;
            }

            if (currentPage === this.currentPage) {
                return;
            }

            this.currentPage = currentPage;

            /**
             * Fired when the current page is changed by pagination navigation.
             *
             * @event paging-clicked
             * @type {number}
             */
            this.$emit('paging-clicked', this.currentPage);
        },
        isPageActive(page) {
            const currentItem = parseInt(page, 10);

            if (isNaN(currentItem)) {
                return false;
            }

            return currentItem === this.currentPage;
        },
    },
};
</script>

<style lang="scss" src="@v4/common/components/molecules/DkPagination/_index.scss"></style>
